import { setHeaderHeight } from "../helpers/header_b";

/**
 * Fetch products user has search for in the search overlay
 */
export class Search {
    /**
     * Creates a new Search instance.
     *
     * @param {string} csrf - CSRF token for API requests.
     * @param {string} url - Base url for searching.
     * @param {string} overlayId - HTML element for overlay container.
     * @param {string} overlayContentId - HTML element for search results.
     * @param {string} initialSearchFormId - Initial search form before overlay is shown.
     * @param {string} initialSearchInputId - Initial search input field before overlay is shown.
     * @param {string} overlaySearchInputId - Search input field when overlay is shown.
     * @param {string} mobileSearchId - Search icon on smartphones.
     * @param {string} magnifyGlassSpinnerId - Shown spinner when fetching results.
     * @param {string} magnifyGlassId - Submit search to dedicated search page.
     * @param {string} clearId - For clearing search input field.
     * @param {string} closeId - Close overlay.
     */
    constructor(
        csrf,
        url,
        overlayId,
        overlayContentId,
        initialSearchFormId,
        initialSearchInputId,
        overlaySearchInputId,
        mobileSearchId,
        magnifyGlassSpinnerId,
        magnifyGlassId,
        clearId,
        closeId,
    ) {
        const searchShown = false;
        const overlay = document.getElementById(overlayId);
        const overlayContent = document.getElementById(overlayContentId);
        const initialSearchForm = document.getElementById(initialSearchFormId);
        const initialSearchInput =
            document.getElementById(initialSearchInputId);
        const overlaySearchInput =
            document.getElementById(overlaySearchInputId);
        const mobileSearch = document.getElementById(mobileSearchId);
        const magnifyGlassSpinner = document.getElementById(
            magnifyGlassSpinnerId,
        );
        const magnifyGlass = document.getElementById(magnifyGlassId);
        const clear = document.getElementById(clearId);
        const close = document.getElementById(closeId);

        if (
            !csrf ||
            !url ||
            !overlay ||
            !overlayContent ||
            !initialSearchForm ||
            !initialSearchInput ||
            !overlaySearchInput ||
            !mobileSearch ||
            !magnifyGlassSpinner ||
            !magnifyGlass ||
            !clear ||
            !close
        ) {
            throw new Error(`Missing variable(s)`);
        }

        /** @type {string} */
        this.csrf = csrf;

        /** @type {string} */
        this.url = url;

        /** @type {boolean} */
        this.searchShown = searchShown;

        /** @type {HTMLElement} */
        this.overlay = overlay;

        /** @type {HTMLElement} */
        this.overlayContent = overlayContent;

        /** @type {HTMLInputElement} */
        this.overlaySearchInput = /** @type {HTMLInputElement} */ (
            overlaySearchInput
        );

        /** @type {HTMLElement} */
        this.initialSearchForm = initialSearchForm;

        /** @type {HTMLElement} */
        this.magnifyGlassSpinner = magnifyGlassSpinner;

        /** @type {HTMLElement} */
        this.magnifyGlass = magnifyGlass;

        /** @type {HTMLElement} */
        this.closeElement = close;

        /** @type {HTMLElement} */
        this.clearElement = clear;

        /** @type {AbortController | null} */
        this.abortController = null;

        /** @type {ReturnType<typeof setTimeout> | null} */
        this.debounceTimer = null;

        this.search(initialSearchInput, "focus");
        this.search(overlaySearchInput, "input", 300);
        this.search(mobileSearch, "click");

        if (mobileSearch.firstElementChild?.tagName === "IMG") {
            this.search(
                /** @type {HTMLElement} */ (mobileSearch.firstElementChild),
                "click",
            );
        }

        this.close(overlay);
        this.close(close);
        this.clear(clear);
        this.tracking();
        this.categoriesSeeMore();
    }

    /**
     * Perform a fetch request with CSRF token.
     *
     * @param {string} term - Search term user has used.
     * @param {AbortSignal} signal - Aborts the request when a newer one supersedes it.
     * @returns {Promise<string | null>} HTML response, or null if the request was superseded.
     */
    getData(term, signal) {
        /** @type {RequestInit} */
        const options = {
            headers: {
                "Content-Type": "text/html",
                "X-CSRF-Token": this.csrf ?? "",
            },
            method: "GET",
            signal,
        };

        return fetch(this.url + encodeURIComponent(term), options)
            .then((response) => {
                if (response.ok) {
                    return response.text();
                }

                return Promise.reject({});
            })
            .then((data) => {
                return data;
            })
            .catch((data) => (data?.name === "AbortError" ? null : data));
    }

    /**
     * Cancel any in-flight search request and fetch results for the current input value.
     *
     * Cancellation prevents a slower, stale response for an earlier keystroke from
     * overwriting the results of a later one.
     *
     * @returns {void}
     */
    fetchResults() {
        this.abortController?.abort();
        this.abortController = new AbortController();

        this.getData(
            this.overlaySearchInput.value,
            this.abortController.signal,
        ).then((data) => {
            if (data === null) {
                return;
            }

            this.overlayContent.innerHTML = data;
            this.magnifyGlassSpinner.classList.add("d-none");
            this.magnifyGlass.classList.remove("d-none");
            this.updateSearchActiveState();
            this.searchTermTracking(this.overlaySearchInput.value);
        });
    }

    /**
     * Add listener for triggering retriveal of search results.
     *
     * @param {HTMLElement} el - Search input field.
     * @param {string} action - event type like focus, input, click etc.
     * @param {number} [debounceMs] - Delay before firing the request, reset on every event; 0 fires immediately.
     * @returns {void}
     */
    search(el, action, debounceMs = 0) {
        el.addEventListener(action, () => {
            this.magnifyGlass.classList.add("d-none");
            this.magnifyGlassSpinner.classList.remove("d-none");

            if (this.searchShown === false) {
                document.body.classList.add("show-search");
                setHeaderHeight();
                this.searchShown = true;
            }

            this.overlaySearchInput.focus();

            if (this.overlaySearchInput.value !== "") {
                this.clearElement.classList.remove("d-none");
            } else {
                this.clearElement.classList.add("d-none");
            }

            if (this.debounceTimer !== null) {
                clearTimeout(this.debounceTimer);
            }

            this.debounceTimer = setTimeout(() => {
                this.fetchResults();
            }, debounceMs);
        });
    }

    /**
     * Clear search input field
     *
     * @param {HTMLElement} el - Clear search input button
     */
    clear(el) {
        el.addEventListener("click", () => {
            this.overlaySearchInput.value = "";
            this.magnifyGlassSpinner.classList.remove("d-none");
            this.magnifyGlass.classList.add("d-none");
            el.classList.add("d-none");
            this.overlaySearchInput.focus();

            if (this.debounceTimer !== null) {
                clearTimeout(this.debounceTimer);
            }

            this.fetchResults();
        });
    }

    /**
     * Close overlay
     *
     * @param {HTMLElement} el - Close search overlay.
     */
    close(el) {
        el.addEventListener("click", (/** @type {Event} */ e) => {
            const target = /** @type {HTMLSelectElement} */ (e.target);

            if (target.parentElement !== this.closeElement && target !== el) {
                return;
            }

            this.searchShown = false;
            document.body.classList.remove("show-search");
            setHeaderHeight();
            this.overlayContent.innerHTML =
                '<div class="logo-loader"><div class="logo-spinner"></div></div>';
        });
    }

    /**
     * Toggle the "search-active" class on the overlay depending on
     * whether the search input currently holds a value.
     *
     * @returns {void}
     */
    updateSearchActiveState() {
        if (this.overlaySearchInput.value !== "") {
            this.overlay.classList.add("search-active");
        } else {
            this.overlay.classList.remove("search-active");
        }
    }

    /**
     * Attach the delegated tracking listeners to the overlay.
     *
     * The overlay content is replaced with `innerHTML` on every search, so the
     * listeners live on the overlay itself and resolve the clicked result via
     * `closest()`. Called once from the constructor — re-attaching per search
     * would push one duplicate event per accumulated listener.
     *
     * @returns {void}
     */
    tracking() {
        this.overlay.addEventListener("click", (/** @type {Event} */ e) => {
            const target = e.target;

            if (!(target instanceof Element)) {
                return;
            }

            const el = /** @type {HTMLElement|null} */ (
                target.closest(
                    ".search-track-add-to-cart, .search-track-product, .search-track-category, .search-track-brand, .search-track-submenu, #search-see-all-products",
                )
            );

            if (!el) {
                return;
            }

            const term = this.overlaySearchInput.value;

            if (el.classList.contains("search-track-add-to-cart")) {
                this.addToCartTracking(el, term);
            } else if (el.classList.contains("search-track-product")) {
                this.productTracking(el, term);
            } else if (el.classList.contains("search-track-category")) {
                this.categoryTracking(el, term);
            } else if (el.classList.contains("search-track-brand")) {
                this.brandTracking(el, term);
            } else if (el.classList.contains("search-track-submenu")) {
                this.sectionTracking(el, term);
            } else {
                this.submitSearchTracking(term);
            }
        });

        this.overlay.addEventListener("submit", () => {
            this.submitSearchTracking(this.overlaySearchInput.value);
        });
    }

    /**
     * Attach the delegated "see more categories" listener to the overlay.
     *
     * The overlay content is replaced with `innerHTML` on every search, so the
     * listener lives on the overlay itself and resolves the clicked link via
     * `closest()`. Called once from the constructor.
     *
     * @returns {void}
     */
    categoriesSeeMore() {
        this.overlay.addEventListener("click", (/** @type {Event} */ e) => {
            const target = e.target;

            if (!(target instanceof Element)) {
                return;
            }

            const el = /** @type {HTMLElement|null} */ (
                target.closest(".lux-search-categories-see-more")
            );

            if (!el) {
                return;
            }

            el.classList.add("d-none");
            el.nextElementSibling?.classList.remove("d-none");
        });
    }

    /**
     * Track a click on a product result.
     *
     * @param {HTMLElement} el - Clicked product element.
     * @param {string} term - Search term user has used.
     * @returns {void}
     */
    productTracking(el, term) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "search_click",
            search_term: term || null,
            click_type: "product",
            item_name: el.dataset.searchTrackName || null,
            item_brand: el.dataset.searchTrackBrand || null,
            item_id: el.dataset.searchTrackId || null,
        });
    }

    /**
     * Track a click on a category result.
     *
     * @param {HTMLElement} el - Clicked category element.
     * @param {string} term - Search term user has used.
     * @returns {void}
     */
    categoryTracking(el, term) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "search_click",
            search_term: term || null,
            click_type: "category",
            click_label: el.dataset.searchTrackCategory || null,
        });
    }

    /**
     * Track a click on a brand result.
     *
     * @param {HTMLElement} el - Clicked brand element.
     * @param {string} term - Search term user has used.
     * @returns {void}
     */
    brandTracking(el, term) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "search_click",
            search_term: term || null,
            click_type: "brand",
            click_label: el.dataset.searchTrackBrand || null,
        });
    }

    /**
     * Track a click on a submenu item.
     *
     * @param {HTMLElement} el - Clicked submenu element.
     * @param {string} term - Search term user has used.
     * @returns {void}
     */
    sectionTracking(el, term) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "search_click",
            search_term: term || null,
            click_type: "submenu",
            click_label: el.dataset.searchTrackSubmenu || null,
        });
    }

    /**
     * Track an add-to-cart click on a product result.
     *
     * @param {HTMLElement} el - Clicked add-to-cart button.
     * @param {string} term - Search term user has used.
     * @returns {void}
     */
    addToCartTracking(el, term) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "search_click",
            search_term: term || null,
            click_type: "add_to_cart",
            item_name: el.dataset.searchTrackName || null,
            item_brand: el.dataset.searchTrackBrand || null,
            item_id: el.dataset.searchTrackId || null,
        });
    }

    /**
     * Track a submit through to the dedicated search results page.
     *
     * @param {string} term - Search term user has used.
     * @returns {void}
     */
    submitSearchTracking(term) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "view_search_results",
            search_term: term || null,
        });
    }

    /**
     * When user has entered a search term and results has been shown.
     *
     * @param {string} term - Search term user has used.
     * @returns {void}
     */
    searchTermTracking(term) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "search",
            search_term: term || null,
        });
    }
}
